import { useRef, useState } from "react";
import { useNoPrepay, type NoPrepay, type PrepayMark } from "../../hooks/useNoPrepay";
import { PlayersBadge, FlagToggle, NumPadPop } from "../../components/shared";
import { api } from "../../api/client";
import { openTgChat, openTgChatByPhone } from "../../lib/telegram";
import css from "./dcSections.module.css";

const CH_ICO: Record<string, string> = { tg: "✈", vk: "VK", max: "MAX" };

const WD = ["вс", "пн", "вт", "ср", "чт", "пт", "сб"];
const MON = ["янв", "фев", "мар", "апр", "мая", "июн", "июл", "авг", "сен", "окт", "ноя", "дек"];

/**
 * DC «БЕЗ предоплаты» (screen-07). Upcoming active bookings with no prepayment
 * (today → +60d) from /api/bookings/noprepay — DC chases these to collect.
 * Тап по карточке (24.08, владелец) — onOpen: сетка дня брони + модалка (задаток).
 */
export function DcNoPrepaySection({ onOpen }: { onOpen: (b: NoPrepay) => void }) {
  /* 08.09 (Аманда, владелец): отмеченные прямо из списка остаются в нём ещё 5 минут (и после
     перезагрузки вкладки — см. useNoPrepay), можно поправить сумму или снять; счётчик — только неоплаченные */
  const { items, loading, error, eff, mark } = useNoPrepay();
  const left = items.filter((b) => !eff(b).paid).length;
  return (
    <div className={css.main}>
      {error ? (
        <div className={css.err}>Ошибка загрузки: {error}</div>
      ) : loading ? (
        <div className={css.loading}>Загрузка…</div>
      ) : items.length === 0 ? (
        <div className={css.empty}>Броней без предоплаты нет</div>
      ) : (
        <>
          <div className={css.counter}>Найдено: <b>{left}</b> {plural(left)} без предоплаты</div>
          {items.map((b) => <NpCard key={String(b.id)} b={b} onOpen={onOpen} state={eff(b)} onMark={(m) => mark(String(b.id), m)} />)}
        </>
      )}
    </div>
  );
}

/* 08.09 (Аманда, владелец: «кнопочку тут сразу отметить предоплату, чтобы по брони не
   переходить туда-сюда»): та же ячейка «Здтк», что в модалке брони — тот же FlagToggle и
   та же зелёная сумма. Сохраняет сразу тем же PUT, что и модалка (prepaid + prepaid_amount),
   дефолт 1000 ₽ как в модалке; снять галочку — вернуть.
   Владелец, 08.09: сумма правится выпадающей панелью под цифрой (NumPadPop, как ставка в
   дашборде ОО), открытие по клику — шит снизу, открытый на pointerdown, закрывался
   отпусканием того же пальца, поэтому срабатывал только лонгпресс. */
/* Владелец, 08.09 (2): «переключатель сдвигается, конфликтует с цифрами, не с первого раза».
   Ничего не двигается: ячейка стоит абсолютно в правом верхнем углу карточки, подпись
   «Здтк» постоянная, поле суммы есть всегда (выключено — приглушено), ширины фиксированы.
   Без тормозов: состояние переключается мгновенно, PUT уходит в фоне; при ошибке
   откатываемся к последнему подтверждённому сервером состоянию (последний запрос главный). */
function PrepayCell({ b, state, onMark }: { b: NoPrepay; state: PrepayMark; onMark: (m: PrepayMark) => void }) {
  const { paid, amt } = state;
  const [pad, setPad] = useState(false);
  const [err, setErr] = useState("");
  const amtRef = useRef<HTMLInputElement | null>(null);
  const seq = useRef(0);
  const confirmed = useRef<PrepayMark>(state); /* последнее подтверждённое сервером (старт — строка сервера) */
  const save = (p: boolean, v: number) => {
    const my = ++seq.current;
    setErr(""); onMark({ paid: p, amt: v }); /* сразу, не дожидаясь сервера */
    api(`/api/bookings/${b.id}`, { method: "PUT", body: { prepaid: p, prepaid_amount: p ? v : 0 } })
      .then(() => { if (my === seq.current) confirmed.current = { paid: p, amt: v }; })
      .catch((e: unknown) => {
        if (my !== seq.current) return; /* уже есть более поздний запрос — его результат главнее */
        onMark(confirmed.current);
        setErr(e instanceof Error ? e.message : "Не сохранилось");
      });
  };
  return (
    <span className={css.npPay} onClick={(e) => e.stopPropagation()} onPointerDown={(e) => e.stopPropagation()}>
      <FlagToggle checked={paid} onChange={(v) => save(v, amt)} label="Здтк" tone="success" />
      <input ref={amtRef} readOnly inputMode="none" className={`${css.depMini} ${paid ? "" : css.depOff}`} value={amt}
        aria-label="Сумма задатка" title={paid ? "Тап — изменить сумму" : "Тап — отметить предоплату с этой суммой"}
        onPointerDown={(e) => e.preventDefault()} onClick={() => setPad((o) => !o)} />
      <NumPadPop open={pad} anchor={amtRef.current} label="Задаток" mode="money" max={100000} value={amt}
        onCommit={(v) => { if (v !== amt || !paid) save(true, v); }} onClose={() => setPad(false)} />
      {err ? <span className={css.npErr} role="alert">{err}</span> : null}
    </span>
  );
}

function NpCard({ b, onOpen, state, onMark }: { b: NoPrepay; onOpen: (b: NoPrepay) => void; state: PrepayMark; onMark: (m: PrepayMark) => void }) {
  const time = (b.custom_time || b.time || "").slice(0, 5);
  const quest = b.special_version_title || b.short_name || b.quest_name || "—";
  const client = (b.client_name ?? "").trim();
  const phone = (b.phone ?? "").trim();
  return (
    <div className={`${css.npCard} ${state.paid ? css.npPaid : ""}`} role="button" tabIndex={0} title="Открыть бронь" onClick={() => onOpen(b)}>
      <div className={css.npRow1}>
        <span className={css.npWhen}>
          <span className={css.npD}>{fmtDM(b.date)}</span>
          <span className={css.npSep}>·</span>
          <span className={css.npWd}>{weekday(b.date)}</span>
          <span className={css.npSep}>·</span>
          <span className={css.npT}>{time}</span>
        </span>
        <span className={css.npQuest}>{quest}</span>
        <PrepayCell b={b} state={state} onMark={onMark} />
      </div>
      <div className={css.npRow2}>
        {client ? <span className={css.cxClient}>{client}</span> : null}
        {/* 05.09 (владелец): стандартный бейдж игроков — клик открывает карточку клиента (всплытие гасит сам, тап по карточке не срабатывает) */}
        {b.players ? <PlayersBadge count={b.players} bookingId={Number(b.id)} fallbackName={client} size="sm" /> : null}
        {phone ? (
          <a className={css.npPhone} href={`tel:${phone.replace(/[^\d+]/g, "")}`} onClick={(e) => e.stopPropagation()}>
            <svg viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true"><path d="M2 3a1 1 0 0 1 1-1h1.5l1 2-1 1a6 6 0 0 0 3 3l1-1 2 1V9.5a1 1 0 0 1-1 1A9 9 0 0 1 2 3z" /></svg>
            {phone}
          </a>
        ) : null}
        {/* 26.08 (владелец): куда клиенту написано (✈/VK/MAX) + юзернейм; тап — переписка (канон формы брони) */}
        {(() => {
          const ch = (b.channel ?? "").trim();
          const tgRaw = (b.telegram ?? "").trim();
          const tg = tgRaw === "[encrypted]" ? "" : tgRaw;
          if (!ch && !tg) return null;
          const v = tg.replace(/^@/, "");
          const canChat = !!tg || (ch === "tg" && !!phone);
          const open = () => {
            if (tg) { if (/^\d+$/.test(v)) void api(`/api/bookings/${b.id}/open-chat`, { method: "POST" }).catch(() => {}); else openTgChat(tg); }
            else if (phone) openTgChatByPhone(phone);
          };
          const label = `${CH_ICO[ch] ?? (ch || "✈")}${tg ? ` ${/^\d+$/.test(v) ? `id${v}` : `@${v}`}` : ""}`;
          return canChat
            ? <button type="button" className={css.npChat} title="Открыть переписку" onClick={(e) => { e.stopPropagation(); open(); }}>{label}</button>
            : <span className={css.npChatOff} title="канал связи">{label}</span>;
        })()}
      </div>
      {b.comment ? (
        <div className={css.npNote}>
          <svg viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true"><path d="M2 3h8v5H6L3 10V8H2V3z" /></svg>
          <span>{b.comment}</span>
        </div>
      ) : null}
      {b.created_by_name ? <div className={css.npBy}>Бронь: <b>{b.created_by_name}</b></div> : null}
    </div>
  );
}

function plural(n: number): string {
  const m10 = n % 10, m100 = n % 100;
  if (m10 === 1 && m100 !== 11) return "бронь";
  if (m10 >= 2 && m10 <= 4 && (m100 < 10 || m100 >= 20)) return "брони";
  return "броней";
}
function fmtDM(date?: string | null): string {
  if (!date) return "";
  const [, m, d] = date.split("-");
  if (!m || !d) return date;
  return `${Number(d)} ${MON[Number(m) - 1] ?? m}`;
}
function weekday(date?: string | null): string {
  if (!date) return "";
  const dt = new Date(`${date}T00:00:00Z`);
  return WD[dt.getUTCDay()] ?? "";
}
